/**
 * API Service Layer for Sourcity v3
 * Handles all communication with the backend
 */

const API_BASE = import.meta.env.VITE_API_URL || '';

class ApiError extends Error {
  constructor(public status: number, message: string, public data?: unknown) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(
  endpoint: string,
  options: RequestInit = {},
  token?: string | null
): Promise<T> {
  const headers: Record<string, string> = {};

  // Copy existing headers
  if (options.headers) {
    const existingHeaders = options.headers as Record<string, string>;
    Object.assign(headers, existingHeaders);
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Only set Content-Type for non-FormData requests
  if (!(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new ApiError(response.status, errorData.error || response.statusText, errorData);
  }

  // Check if response is JSON
  const contentType = response.headers.get('content-type');
  if (contentType?.includes('application/json')) {
    return response.json();
  }

  return response as unknown as T;
}

// ============================================
// Library / Materials API
// ============================================

export interface Material {
  id: string;
  filename: string;
  original_filename: string;
  file_type: string;
  file_size: number;
  upload_date: string;
  status: string;
  page_count?: number;
  word_count?: number;
  thumbnail_url?: string;
}

export type UploadStatus = 'queued' | 'processing' | 'completed' | 'failed' | 'cancelled';
export interface UploadStatusResponse {
  success: boolean;
  sessionId: string;
  /** Canonical status; the legacy status/stage fields remain available. */
  uploadStatus: UploadStatus;
  status: UploadStatus;
  progress: number;
  stage: string;
  stageCode?: string;
  materialId?: string;
  details?: string;
  error?: string;
}

export function normalizeUploadStatusResponse(value: unknown, sessionId: string): UploadStatusResponse {
  const data = value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown> : null;
  if (!data || data.success !== true || data.sessionId !== sessionId) {
    throw new ApiError(502, 'The upload status response is invalid.', { code: 'UPLOAD_STATUS_INVALID' });
  }
  const raw = typeof data.uploadStatus === 'string' ? data.uploadStatus : data.status;
  const normalized = typeof raw === 'string' ? raw.toLowerCase() : '';
  const status = normalized === 'error' ? 'failed' : normalized === 'canceled' ? 'cancelled' : normalized;
  if (!['queued', 'processing', 'completed', 'failed', 'cancelled'].includes(status)) {
    throw new ApiError(502, 'The upload status response is invalid.', { code: 'UPLOAD_STATUS_INVALID' });
  }
  return {
    success: true, sessionId, status: status as UploadStatus, uploadStatus: status as UploadStatus,
    progress: typeof data.progress === 'number' && Number.isFinite(data.progress) ? data.progress : 0,
    stage: typeof data.stage === 'string' ? data.stage : '',
    ...(typeof data.stageCode === 'string' ? { stageCode: data.stageCode } : {}),
    ...(typeof data.materialId === 'string' ? { materialId: data.materialId } : {}),
    ...(typeof data.details === 'string' ? { details: data.details } : {}),
    ...(typeof data.error === 'string' ? { error: data.error } : {}),
  };
}

export interface LibraryResponse {
  success: boolean;
  materials: Material[];
}

export const libraryApi = {
  async getLibrary(token: string): Promise<LibraryResponse> {
    const response = await request<{ success: boolean; library: Material[] }>('/api/library', { method: 'GET' }, token);
    // Backend returns 'library', frontend expects 'materials'
    return { success: response.success, materials: response.library || [] };
  },

  async uploadFile(
    file: File,
    token: string,
    onProgress?: (progress: number) => void
  ): Promise<{ success: boolean; sessionId: string }> {
    const formData = new FormData();
    formData.append('pdf', file);

    // For progress tracking, we need XMLHttpRequest
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable && onProgress) {
          const progress = (e.loaded / e.total) * 100;
          onProgress(progress);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            resolve(JSON.parse(xhr.responseText));
          } catch {
            reject(new ApiError(502, 'Upload completed but the server returned an invalid response. Please try again.'));
          }
        } else {
          let payload: any = {};
          try {
            payload = JSON.parse(xhr.responseText || '{}');
          } catch {
            payload = {};
          }
          reject(new ApiError(
            xhr.status,
            payload.message || payload.error || xhr.statusText || 'Upload failed',
            payload
          ));
        }
      });

      xhr.addEventListener('error', () => {
        reject(new ApiError(0, 'Network error'));
      });

      xhr.addEventListener('timeout', () => {
        reject(new ApiError(408, 'The upload connection timed out. Please try again.'));
      });

      xhr.addEventListener('abort', () => {
        reject(new ApiError(0, 'The upload was cancelled.'));
      });

      xhr.open('POST', `${API_BASE}/api/upload`);
      // Bound a half-open connection without penalizing slower 50MB uploads.
      // Server-side content hashing makes a retry idempotent if the response
      // was lost after the server accepted the file.
      xhr.timeout = 10 * 60 * 1000;
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.send(formData);
    });
  },

  async getUploadStatus(sessionId: string, token: string): Promise<UploadStatusResponse> {
    const response = await request<unknown>(`/api/upload-status/${encodeURIComponent(sessionId)}`, { method: 'GET' }, token);
    return normalizeUploadStatusResponse(response, sessionId);
  },

  async deleteMaterial(materialId: string, token: string): Promise<{ success: boolean }> {
    return request(`/api/material/${materialId}`, { method: 'DELETE' }, token);
  },

  async getMaterialContent(
    materialId: string,
    startPage: number,
    token: string
  ): Promise<{ success: boolean; content: string }> {
    return request(
      `/api/content/${materialId}`,
      {
        method: 'POST',
        body: JSON.stringify({ startPage }),
      },
      token
    );
  },
};

// ============================================
// Notes API
// ============================================

export interface Note {
  id: string;
  note_type: string;
  selected_text?: string;
  note_content: string;
  page_number?: number;
  document_id?: string;
  document_title?: string;
  created_at: string;
  updated_at: string;
}

export const notesApi = {
  async getNotes(token: string, documentId?: string): Promise<{ success: boolean; notes: Note[] }> {
    const endpoint = documentId ? `/api/notes?documentId=${documentId}` : '/api/notes';
    return request(endpoint, { method: 'GET' }, token);
  },

  async saveNote(note: Partial<Note>, token: string): Promise<{ success: boolean; noteId: string }> {
    return request('/api/notes', { method: 'POST', body: JSON.stringify(note) }, token);
  },

  async updateNote(noteId: string, note: Partial<Note>, token: string): Promise<{ success: boolean }> {
    return request(`/api/notes/${noteId}`, { method: 'PUT', body: JSON.stringify(note) }, token);
  },

  async deleteNote(noteId: string, token: string): Promise<{ success: boolean }> {
    return request(`/api/notes/${noteId}`, { method: 'DELETE' }, token);
  },
};

// ============================================
// Annotations API (XFDF)
// ============================================

function invalidAnnotationResponse(): ApiError {
  return new ApiError(502, 'The annotation response is invalid.', {
    code: 'ANNOTATION_RESPONSE_INVALID',
  });
}

function requireAnnotationSuccess(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw invalidAnnotationResponse();
  }
  const payload = value as Record<string, unknown>;
  if (payload.success === false) {
    throw new ApiError(502, typeof payload.error === 'string' && payload.error
      ? payload.error
      : 'The annotation request failed.', { code: 'ANNOTATION_REQUEST_FAILED' });
  }
  if (payload.success !== true) throw invalidAnnotationResponse();
  return payload;
}

export const annotationsApi = {
  async getAnnotations(
    documentId: string,
    token: string
  ): Promise<{ success: boolean; xfdf?: string }> {
    const response = requireAnnotationSuccess(await request<unknown>(
      `/api/annotations/${encodeURIComponent(documentId)}`, { method: 'GET' }, token,
    ));
    // The server wraps the XFDF result, while older clients and fixtures use
    // a flat response. An explicit nested failure must never become empty data.
    const annotations = 'annotations' in response
      ? requireAnnotationSuccess(response.annotations)
      : response;
    if (typeof annotations.xfdf === 'string') {
      return { success: true, xfdf: annotations.xfdf };
    }
    if (annotations.format === 'legacy' && Array.isArray(annotations.annotations)) {
      if (annotations.annotations.length === 0) return { success: true, xfdf: '' };
      // The XFDF reader cannot restore legacy coordinates. Treating them as an
      // empty document would allow the next autosave to overwrite saved marks.
      throw new ApiError(502, 'The saved annotations require legacy-format support.', {
        code: 'ANNOTATION_LEGACY_UNSUPPORTED',
      });
    }
    throw invalidAnnotationResponse();
  },

  async saveAnnotations(
    documentId: string,
    xfdf: string,
    token: string
  ): Promise<{ success: boolean }> {
    const response = requireAnnotationSuccess(await request<unknown>(
      `/api/annotations/${encodeURIComponent(documentId)}`,
      {
        method: 'POST',
        body: JSON.stringify({ xfdf, action: 'save' }),
      },
      token
    ));
    if ('result' in response) requireAnnotationSuccess(response.result);
    return { success: true };
  },
};

// ============================================
// AI / Chat API
// ============================================

export interface ChatResponse {
  choices: Array<{
    message: {
      role: string;
      content: string;
    };
    finish_reason: string;
  }>;
  speech?: {
    audioBase64: string;
    mimeType: string;
    language: string;
  };
  speechUnavailableReason?: 'daily_limit';
}

export const aiApi = {
  async explainSelection(
    selectedText: string,
    token: string,
    language: 'en' | 'ar' = 'en',
    includeSpeech = false
  ): Promise<ChatResponse> {
    return request(
      '/api/explain-selection',
      {
        method: 'POST',
        body: JSON.stringify({
          task: 'explain_selection',
          selectedText,
          language,
          includeSpeech,
        }),
      },
      token
    );
  },

  async qa(
    materialId: string,
    query: string,
    token: string
  ): Promise<{ success: boolean; answer: string; sources?: Array<{ materialId?: string; filename?: string; excerpt?: string; confidence?: number } | string> }> {
    return request(
      '/api/ask',
      {
        method: 'POST',
        body: JSON.stringify({ question: query, materialId }),
      },
      token
    );
  },
};

// ============================================
// Page Audio API (Pre-recorded TTS)
// ============================================

export interface AudioStatus {
  ready: number;
  processing: number;
  pending: number;
  error: number;
  total: number;
  isComplete: boolean;
}

export interface SpeechMark {
  time: number;
  type: 'word' | 'sentence';
  value: string;
  start?: number;
  end?: number;
  // Index of the sentence this word mark belongs to (word marks only) —
  // used to highlight a whole sentence at a time during read-along playback.
  sentenceIndex?: number;
}

export interface WordPosition {
  timeMs: number;
  timeSeconds: number;
  wordIndex: number;
  endTimeMs: number | null;
  endTimeSeconds: number | null;
  endWordIndexExclusive: number;
  matchedWord: string;
  fuzzyMatch: boolean;
}

export interface AudioJobStatus {
  status: string;
  totalPages: number;
  processedPages: number;
  startedAt?: string;
  completedAt?: string;
  error?: string;
}

export interface PageAudioStatus {
  pageNumber: number;
  status: 'missing' | 'pending' | 'processing' | 'ready' | 'error';
  isReady: boolean;
  error?: string | null;
}

export const pageAudioApi = {
  /**
   * Get audio generation status for a document
   */
  async getStatus(
    documentId: string,
    token: string,
    pageNumber?: number
  ): Promise<{ 
    success: boolean; 
    audio: AudioStatus; 
    job: AudioJobStatus | null;
    page: PageAudioStatus | null;
    isReady: boolean; 
    message: string;
  }> {
    const query = pageNumber ? `?pageNumber=${pageNumber}` : '';
    return request(`/api/audio/document/${documentId}/status${query}`, { method: 'GET' }, token);
  },

  async generatePageAudio(
    documentId: string,
    pageNumber: number,
    token: string
  ): Promise<{
    success: boolean;
    documentId: string;
    pageNumber: number;
    status: string;
    cached: boolean;
  }> {
    return request(
      `/api/audio/document/${documentId}/page/${pageNumber}/generate`,
      { method: 'POST' },
      token
    );
  },

  /**
   * Get presigned URL for page audio
   */
  async getPageAudioUrl(
    documentId: string,
    pageNumber: number,
    token: string
  ): Promise<{ 
    success: boolean; 
    audioUrl: string; 
    durationMs: number;
    expiresIn: number;
  }> {
    return request(`/api/audio/document/${documentId}/page/${pageNumber}/url`, { method: 'GET' }, token);
  },

  /**
   * Get speech marks for word-level seeking
   */
  async getSpeechMarks(
    documentId: string,
    pageNumber: number,
    token: string
  ): Promise<{ 
    success: boolean; 
    marks: SpeechMark[];
    wordCount: number;
    sentenceCount: number;
  }> {
    return request(`/api/audio/document/${documentId}/page/${pageNumber}/marks`, { method: 'GET' }, token);
  },

  /**
   * Find word position in audio for seek-to-selection
   */
  async findWordPosition(
    documentId: string,
    pageNumber: number,
    text: string,
    token: string,
    startFrom = 0
  ): Promise<{ 
    success: boolean; 
    position: WordPosition;
  }> {
    return request(
      `/api/audio/document/${documentId}/page/${pageNumber}/find-position`,
      {
        method: 'POST',
        body: JSON.stringify({ text, startFrom }),
      },
      token
    );
  },

  /**
   * Get streaming URL for page audio (direct fetch, not presigned)
   */
  getStreamUrl(documentId: string, pageNumber: number, token: string): string {
    return `${API_BASE}/api/audio/document/${documentId}/page/${pageNumber}?token=${encodeURIComponent(token)}`;
  },
};

// ============================================
// Flashcards / Quiz API
// ============================================

export interface Flashcard {
  id: string;
  front: string;
  back: string;
  deck_id: string;
  material_id?: string;
  created_at: string;
}

export interface FlashcardDeck {
  id: string;
  name: string;
  description?: string;
  card_count: number;
  created_at: string;
}

export const flashcardsApi = {
  async getDecks(token: string): Promise<{ success: boolean; decks: FlashcardDeck[] }> {
    return request('/api/flashcards/decks', { method: 'GET' }, token);
  },

  async getDeckCards(
    deckId: string,
    token: string
  ): Promise<{ success: boolean; cards: Flashcard[] }> {
    return request(`/api/flashcards/decks/${deckId}/cards`, { method: 'GET' }, token);
  },

  async generateFlashcards(
    materialId: string,
    count: number,
    token: string
  ): Promise<{ success: boolean; flashcards: Flashcard[] }> {
    return request(
      '/api/flashcards/generate',
      {
        method: 'POST',
        body: JSON.stringify({ materialId, quantity: count }),
      },
      token
    );
  },
};

// ============================================
// MCQ (Multiple Choice Questions) API
// ============================================

export interface MCQQuestion {
  question: string;
  options: string[];
  correctAnswer: number;
  explanation: string;
}

export interface ChallengeTrial {
  status: 'available' | 'pending' | 'completed' | 'recovery_required';
  challenge: {
    id: string;
    materialId: string;
    materialName: string;
    questions: MCQQuestion[];
    createdAt: string;
  } | null;
}

export interface ChallengeAccess {
  success: true;
  paid: boolean;
  maxQuestions: 3 | 50;
  trial: ChallengeTrial;
}

function parseChallengeTrial(value: unknown): ChallengeTrial {
  const trial = value as ChallengeTrial | undefined;
  const invalid = () => new ApiError(502, 'The challenge response is invalid.', { code: 'CHALLENGE_UNAVAILABLE' });
  if (!trial || !['available', 'pending', 'completed', 'recovery_required'].includes(trial.status)) throw invalid();
  const c = trial.challenge;
  if (c !== null && (!c || trial.status !== 'completed'
    || typeof c.id !== 'string' || !c.id || typeof c.materialId !== 'string' || !c.materialId
    || typeof c.materialName !== 'string' || typeof c.createdAt !== 'string' || !Number.isFinite(Date.parse(c.createdAt))
    || !Array.isArray(c.questions) || c.questions.length < 1 || c.questions.length > 3
    || c.questions.some(q => !q || typeof q.question !== 'string' || !q.question.trim()
      || !Array.isArray(q.options) || q.options.length !== 4 || q.options.some(o => typeof o !== 'string')
      || !Number.isInteger(q.correctAnswer) || q.correctAnswer < 0 || q.correctAnswer >= q.options.length
      || typeof q.explanation !== 'string'))) throw invalid();
  return trial;
}

export const challengeTrialApi = {
  async status(token: string): Promise<ChallengeAccess> {
    const response = await request<ChallengeAccess>('/api/challenge-trial', {}, token);
    if (!response || response.success !== true || typeof response.paid !== 'boolean'
      || response.maxQuestions !== (response.paid ? 50 : 3)) {
      throw new ApiError(502, 'The challenge access response is invalid.', { code: 'CHALLENGE_UNAVAILABLE' });
    }
    return { ...response, trial: parseChallengeTrial(response.trial) };
  },
};

export const mcqApi = {
  async generate(
    materialId: string,
    token: string,
    options?: {
      quantity?: number;
      difficulty?: 'easy' | 'medium' | 'hard';
      type?: 'comprehension' | 'factual' | 'analytical';
    }
  ): Promise<{ success: boolean; questions: MCQQuestion[]; trial?: ChallengeTrial; replayed?: boolean }> {
    const response = await request<{ success: boolean; mcqs?: MCQQuestion[]; questions?: MCQQuestion[]; trial?: ChallengeTrial; replayed?: boolean }>(
      '/api/mcq/generate',
      {
        method: 'POST',
        body: JSON.stringify({
          materialId,
          quantity: options?.quantity,
          difficulty: options?.difficulty || 'medium',
          type: options?.type || 'comprehension',
        }),
      },
      token
    );
    // Server returns 'mcqs', frontend expects 'questions'
    return {
      success: response.success,
      questions: response.mcqs || response.questions || [],
      ...(response.trial ? { trial: parseChallengeTrial(response.trial), replayed: response.replayed === true } : {}),
    };
  },
};

// ============================================
// Public pricing (geo via Cloudflare / edge)
// ============================================

export interface PublicPricing {
  countryCode: string;
  isSaudiArabia: boolean;
  currency: 'USD' | 'SAR';
  monthlyAmount: number;
  annualAmount: number;
  promo: {
    eligible: boolean;
    headline?: string;
    detail?: string;
  };
}

export const pricingApi = {
  async getPublicPricing(): Promise<PublicPricing> {
    const r = await request<{ success: boolean } & PublicPricing>('/api/public/pricing', { method: 'GET' });
    if (r.success !== true) throw new ApiError(503, 'Billing prices are unavailable', { code: 'BILLING_UNAVAILABLE' });
    const { success: _ok, ...pricing } = r;
    return pricing;
  },
};

// ============================================
// Subscription / User API
// ============================================

export interface UserProfile {
  id: string;
  email: string;
  display_name?: string;
  subscription_tier: 'free' | 'pro' | 'paid' | 'premium';
  subscription_status?: string;
  created_at: string;
}

export interface SubscriptionStatus {
  success: boolean;
  tier: string;
  hasActiveSubscription: boolean;
  canManageBilling: boolean;
  subscription: {
    status: string;
    currentPeriodEnd: string | number | null;
    cancelAtPeriodEnd: boolean;
    subscriptionType: string;
  } | null;
}

export interface UsageLimits {
  tier: string;
  subscription?: {
    type?: string;
    id?: string;
    date?: string;
  };
  pdf: { current: number; max: number | null; unlimited: boolean };
  pdfSize: { max: number; unit: string };
  tts: { current: number; max: number | null; unlimited: boolean; resetsDaily: boolean; unit: string };
  bookAudio?: { maxPages: number; maxWords: number; preGeneratedPages: number; perBook: true };
  askAi?: { allowed: boolean; paidOnly: true };
  explain: { current: number; max: number | null; unlimited: boolean; resetsDaily: boolean };
  realtime: { current: number; max: number | null; unlimited: boolean; resetsDaily: boolean; unit: string };
}

export const userApi = {
  async getProfile(token: string): Promise<{ success: boolean; user: UserProfile }> {
    // Production returns user_tier; retain the prior subscription_tier alias.
    type ServerProfile = Omit<UserProfile, 'subscription_tier'> & {
      user_tier?: UserProfile['subscription_tier'] | null;
      subscription_tier?: UserProfile['subscription_tier'];
    };
    const response = await request<{ success: boolean; profile: ServerProfile }>(
      '/api/user/profile',
      { method: 'GET' },
      token
    );
    return {
      success: response.success,
      user: {
        ...response.profile,
        subscription_tier: response.profile.user_tier ?? response.profile.subscription_tier ?? 'free',
      },
    };
  },

  async getUsageLimits(token: string): Promise<{ success: boolean; limits: UsageLimits }> {
    return request('/api/user/limits', { method: 'GET' }, token);
  },

  async createCheckoutSession(
    planType: 'monthly' | 'annual',
    token: string,
    locale: 'ar' | 'en' = 'en'
  ): Promise<{ success: boolean; url: string; sessionId: string }> {
    return request(
      '/api/subscription/create',
      {
        method: 'POST',
        body: JSON.stringify({ planType, locale }),
      },
      token
    );
  },

  async createPortalSession(token: string, locale: 'ar' | 'en' = 'en'): Promise<{ success: boolean; url: string }> {
    return request('/api/subscription/portal', { method: 'POST', body: JSON.stringify({ locale }) }, token);
  },

  async getSubscriptionStatus(token: string, signal?: AbortSignal): Promise<SubscriptionStatus> {
    return request('/api/subscription/status', { method: 'GET', signal }, token);
  },

  async getCheckoutStatus(sessionId: string, token: string, signal?: AbortSignal): Promise<{ success: boolean; status: 'active' | 'processing' | 'expired' | 'open' | 'needs_support' }> {
    return request(`/api/subscription/checkout-status?session_id=${encodeURIComponent(sessionId)}`, { method: 'GET', signal }, token);
  },
};

export { ApiError };
